import MomentUtils from "@date-io/moment";
import { IconButton, makeStyles } from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import clsx from "clsx";
import moment from "moment";
import "moment/locale/pt-br";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { lang } from "../../../i18nLang";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialogContent-root.MuiPickersModal-dialog": {
      maxWidth: 600,
      minWidth: "unset",
    },
  },
  dayWrapper: {
    "& .day": {
      width: 36,
      height: 36,
      fontSize: "0.75rem",
      fontWeight: 500,
      color: "inherit",
      margin: "0 2px",
      padding: 0,
    },
    "& .daySelected": {
      background: theme.palette.primary.main,
      color: `${theme.palette.common.white} !important`,
      fontWeight: 600,
    },
    "& .dayDisabled": {
      color: theme.palette.text.disabled,
      pointerEvents: "none",
    },
    "& .current": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    "& .hidden": {
      opacity: 0,
      pointerEvents: "none",
    },
    "& .nonCurrentMonthDay": {
      color: theme.palette.text.disabled,
    },
  },
  highlight: {
    background: theme.palette.primary.light,

    "&.firstHighlight": {
      borderTopLeftRadius: "50%",
      borderBottomLeftRadius: "50%",

      "& .daySelected, & .current": {
        left: -1,
      },
    },
    "&.endHighlight": {
      borderTopRightRadius: "50%",
      borderBottomRightRadius: "50%",

      "& .daySelected, & .current": {
        right: -1,
      },
    },
    "& .current": {
      border: `1px solid ${theme.palette.secondary.dark}`,
    },
  },
  inputPlaceholder: {
    "& input": {
      fontSize: "0.875rem",
      color: theme.palette.gray[400],
    },
  },
}));

moment.locale("pt-br");

interface IProps {
  [property: string]: any;

  /** Label do input. */
  label?: string;
  /** Data inicial atual do período. */
  initialDate: Date | null;
  /** Data final atual do período. */
  finalDate: Date | null;
  /** Label a ser exibida quando a data for nula. */
  emptyLabel?: string;
  /** Formato da data a ser utilizado. */
  format?: string;
  /** Abrir/Fechar picker. */
  open?: boolean;
  /** Função que será chamada ao abrir o picker. */
  onOpen?: () => void;
  /** Função que será chamada ao fechar o picker. */
  onClose?: () => void;
  /** Valor do input. */
  /** Função que será chamada ao alterar o valor do campo. */
  onChange?: (rangeDate: { start: Date | null; end: Date | null }) => void;
}

/**
 * ##### Campo com picker para escolher data.
 * Para ver mais detalhes sobre suas propriedades e métodos acesse: https://material-ui-pickers.dev/api/KeyboardDatePicker.
 *
 * > Para as converções de datas é utitlizada a biblioteca **@date-io/moment**
 * @category Components
 * */
const DateRangePickerField = ({
  className,
  label,
  format,
  initialDate = null,
  finalDate = null,
  onChange,
  emptyLabel,
  ...props
}: IProps) => {
  const classes = useStyles();
  const [current, setCurrent] = useState(0);
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(finalDate);

  useEffect(() => {
    setStartDate(initialDate);
    setEndDate(finalDate);
  }, [initialDate, finalDate]);

  const handleClickDay = (dateMoment) => (ev) => {
    const selectedDate = dateMoment?.toDate();

    if (selectedDate < (startDate || new Date())) {
      updateStartDate(selectedDate);
    } else if (selectedDate > (endDate || new Date())) {
      updateEndDate(selectedDate);
    } else {
      if (current === 0) {
        updateStartDate(selectedDate);
      } else {
        updateEndDate(selectedDate);
      }
    }

    function updateStartDate(date: Date) {
      setStartDate(date);
      setCurrent(1);
      if (!endDate) setEndDate(new Date(date));
    }

    function updateEndDate(date: Date) {
      setEndDate(date);
      setCurrent(0);
      if (!startDate) setStartDate(new Date(date));
    }
  };

  const handleChange = (dateMoment, value) => {
    let dates = {
      start: startDate,
      end: endDate,
    };

    if (dateMoment === null) {
      dates = {
        start: null,
        end: null,
      };
      clearDates();
    }

    if (onChange) onChange(dates);
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrent(0);
  };

  const formatDates = () => {
    const options: any = { month: "2-digit", day: "2-digit", year: "2-digit" };
    const initial = startDate?.toLocaleDateString("pt-br", options) || "";
    const final = endDate?.toLocaleDateString("pt-br", options) || "";
    return `${initial} - ${final}`;
  };

  const handleKeyDown = (ev) => {
    const acceptKeys = ["ArrowLeft", "ArrowRight", "End", "Home"];
    if (acceptKeys.includes(ev.key)) return;

    ev.preventDefault();
    ev.stopPropagation();
  };

  return (
    <MuiPickersUtilsProvider
      libInstance={moment}
      utils={MomentUtils}
      locale={"pt-br"}
    >
      <KeyboardDatePicker
        {...props}
        className={clsx(className, {
          [classes.inputPlaceholder]: startDate === null,
        })}
        DialogProps={{
          className: classes.dialog,
        }}
        label={label}
        format={format}
        emptyLabel={startDate === null ? emptyLabel : formatDates()}
        value={null}
        invalidDateMessage={lang("exceptions.invalidDate")}
        cancelLabel={lang("cancel")}
        clearLabel={lang("clear")}
        todayLabel={lang("today")}
        onChange={handleChange}
        clearable={true}
        inputProps={{ onKeyDown: handleKeyDown }}
        renderDay={(day, selectedDate, dayInCurrentMonth, dayComponent) => {
          if (!day || !selectedDate) return dayComponent;

          let dateClone = moment(day.toDate());

          const start = moment(day.toDate()).startOf("week").toDate();
          const end = moment(day.toDate()).endOf("week").toDate();
          const isFirstDay = dateClone.isSame(start, "day");
          const isLastDay = dateClone.isSame(end, "day");

          const status = { ...dayComponent.props };
          const dayIsSelected =
            day.isSame(startDate, "day") || day.isSame(endDate, "day");
          const dayIsBetween =
            dateClone.isBetween(startDate, endDate) || dayIsSelected;

          const wrapperClassName = clsx(classes.dayWrapper, {
            [classes.highlight]: dayIsBetween,
            firstHighlight: isFirstDay || day.isSame(startDate, "day"),
            endHighlight: isLastDay || day.isSame(endDate, "day"),
          });

          const dayClassName = clsx("day", {
            daySelected: dayIsSelected,
            dayDisabled: status.disabled,
            hidden: status.hidden && !dayIsBetween,
            current: status.current && !dayIsSelected,
            nonCurrentMonthDay: !dayInCurrentMonth,
          });

          return (
            <div className={wrapperClassName || ""}>
              <IconButton
                className={dayClassName}
                onClick={handleClickDay(day)}
                onMouseEnter={(ev: any) => {
                  // console.log("onMouseEnter", ev.target);
                }}
              >
                <span> {moment(day.toDate()).format("D")} </span>
              </IconButton>
            </div>
          );
        }}
        KeyboardButtonProps={{
          "aria-label": "change date",
        }}
      />
    </MuiPickersUtilsProvider>
  );
};

DateRangePickerField.propType = {
  label: PropTypes.string,
  format: PropTypes.string.isRequired,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  value: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func,
};

DateRangePickerField.defaultProps = {
  format: "DD/MM/YYYY",
};

export default DateRangePickerField;
